import React, { useState, useMemo, useEffect } from 'react';
import { FormattedMessage, injectIntl, useIntl } from 'react-intl';
import dayjs from 'utils/dayjs';
import PropTypes from 'prop-types';
import { connect, useDispatch, useSelector } from 'react-redux';
import orderBy from 'lodash.orderby';
import { useParams, Link, useLocation } from 'react-router-dom';

import Icon from 'components/commons/Icon';
import { CurrencyFilters } from 'components/CurrencyFilters';

import { fetchBanksExchrates } from 'actions';

import {
  DEFAULT_SOURCE_CURRENCY,
  getBankLogo,
  getHrefBank,
  getHrefAllBanks,
  parseDate,
} from 'utils';

import { getCurrentCity } from 'selectors/cities';

import { currencies } from 'constants/currency';
import { SORT_ORDER } from 'constants/utils';
import {
  Wrapper,
  Header,
  BankLogo,
  Title,
  TableTitleContent,
  TableTitle,
  InputWrapper,
  SearchInput,
  BanksList,
  BanksListWrapper,
  Row,
  BankLink,
  Curr,
  CurrContent,
  Update,
  AllBanks,
  Source,
  Disclaimer,
  DisclaimerIcon,
  TableHead,
  Bottom,
  TableSubTitleContent,
  IconWatch,
  Column,
  BankColumn,
  ColumnUpdate,
  IconDown,
  MobileSort,
  MobileSortContent,
  MobileSortOption,
  ModalSortOptions,
  ModalSortOptionsTitle,
  ModalSortOptionsList,
  ModalSortTab,
  ModalSortTabWrapper,
  MobileRow,
} from './styles';

import SortedTableHeaderCell from '../SortedTableHeaderCell';
import SortedButton from '../SortedButton';
import { getCurrentCurrency } from '../../selectors';

const currencyOptions = [
  {
    id: 'USD',
    IconWatch,
  },
  {
    id: 'EUR',
  },
  {
    id: 'PLN',
  },
];

const rowsTitle = [
  {
    id: 1,
    name: 'banks',
    align: 'left',
  },

  {
    id: 2,
    name: 'cash',
    align: null,
  },
  {
    id: 3,
    name: 'card',
    align: null,
  },
  {
    id: 4,
    name: 'updated',
    align: 'right',
  },
];

const optionsList = [
  {
    name: 'tableHeader.ask.asc',
    key: 'ask',
    order: 'asc',
  },
  {
    name: 'tableHeader.ask.desc',
    key: 'ask',
    order: 'desc',
  },
  {
    name: 'tableHeader.bid.asc',
    key: 'bid',
    order: 'asc',
  },
  {
    name: 'tableHeader.bid.desc',
    key: 'bid',
    order: 'desc',
  },
];

const renderCurrency = price => {
  const bid = price?.bid
    ? Number(price?.bid)
        .toFixed(2)
        .replace('.', ',')
    : '-';
  const ask = price?.ask
    ? Number(price?.ask)
        .toFixed(2)
        .replace('.', ',')
    : '-';
  return (
    <Curr>
      <CurrContent>{`${bid} / ${ask}`}</CurrContent>
    </Curr>
  );
};
const defaultState = {
  cash: { key: null, order: SORT_ORDER.desc },
  card: { key: null, order: SORT_ORDER.desc },
};

const BanksTable = ({
  banksExchrates,
  currencyList,
  quantity,
  title,
  hasCashColumn,
  isClientNavigation,
}) => {
  const dispatch = useDispatch();
  const intl = useIntl();
  const { formatMessage, locale } = intl;
  const { city, currency, date } = useParams();
  const location = useLocation();

  // Перевіряємо, чи useParams повертає порожній об'єкт (головна сторінка)
  const hasParams = Object.keys(useParams()).length > 0;

  const [currentOption, setCurrentOption] = useState(
    DEFAULT_SOURCE_CURRENCY.toUpperCase()
  );
  const [sort, setSort] = useState({
    ...defaultState,
  });
  const [searchText, setSearchText] = useState('');
  const [selectedColumn, setSelectedColumn] = useState(null);
  const [isOpen, setIsOpen] = useState(false);

  const cpp = quantity || 30;

  const currentCity = useSelector(state =>
    city ? getCurrentCity(state, { city }) : null
  );

  const currentCurrency = useSelector(state =>
    getCurrentCurrency(state, { currency })
  );

  useEffect(() => {
    dispatch(
      fetchBanksExchrates({
        intl,
        currency: currency || currencies.USD,
        date,
        city,
        cpp,
      })
    );
  }, [city, currency, date]);

  const onChangeFilter = id => {
    dispatch(
      fetchBanksExchrates({ intl, currency: id.toLowerCase(), date, city, cpp })
    );
    setCurrentOption(id);
    setSearchText('');
  };

  const filteredRowsTitle = useMemo(
    () =>
      !hasCashColumn ? rowsTitle.filter(row => row.name !== 'cash') : rowsTitle,
    [hasCashColumn]
  );

  const banksList = useMemo(() => {
    if (!searchText) {
      // Якщо параметрів немає, фільтруємо банки за `commercialPlacement`
      if (!hasParams) {
        return banksExchrates.filter(bank => bank.commercialPlacement);
      }
      return banksExchrates;
    }

    // Пошук банків за назвою
    const filteredBySearch = banksExchrates.filter(({ name }) =>
      name.toLowerCase().includes(searchText.toLowerCase())
    );

    // Якщо параметрів немає, додатково фільтруємо за `commercialPlacement`
    if (!hasParams) {
      return filteredBySearch.filter(bank => bank.commercialPlacement);
    }

    return filteredBySearch;
  }, [banksExchrates, searchText, hasParams]);

  const sortedBanksList = useMemo(() => {
    if (!selectedColumn) {
      return banksList;
    }

    return orderBy(
      banksList,
      item => item?.[selectedColumn]?.[sort[selectedColumn]?.key],
      sort?.[selectedColumn]?.order
    );
  }, [banksList, sort.cash, sort.card, selectedColumn]);

  const handleSelectColumn = column => {
    setSelectedColumn(column);

    if (selectedColumn) {
      const activeKey = sort[selectedColumn].key;
      const activeOrder = sort[selectedColumn].order;

      setSort({
        ...sort,
        [column]: {
          key: activeKey,
          order: activeOrder,
        },
      });

      return handleOpenSorting();
    }

    return setSort({
      ...sort,
      [column]: {
        key: 'ask',
        order: 'desc',
      },
    });
  };

  const onSort = ({ column, name, direction, type }) => {
    setSelectedColumn(column);

    const order =
      // eslint-disable-next-line no-nested-ternary
      type === 'button'
        ? SORT_ORDER[direction]
        : direction === SORT_ORDER.desc
        ? SORT_ORDER.asc
        : SORT_ORDER.desc;

    setSort({
      ...sort,
      [column]: {
        key: name,
        order,
      },
    });

    handleOpenSorting();
  };

  const handleOpenSorting = () => {
    setIsOpen(!isOpen);
  };

  if (!hasParams && banksExchrates && banksExchrates.length === 0) {
    return null;
  }

  const allBanksButtonProps = isClientNavigation
    ? {
        as: Link,
        to: `${location.pathname}${currentOption.toLowerCase()}/${
          date ? `${date}/` : ''
        }`,
      }
    : {
        href: getHrefAllBanks({
          currency,
          city,
          date,
          locale,
        }),
      };

  return (
    <Wrapper>
      <Header>
        {title ? (
          <Title>
            <FormattedMessage id={title} />
          </Title>
        ) : (
          <Title>
            {currentCity ? (
              <FormattedMessage
                id="table.banks.mainTitle.city"
                values={{
                  city: currentCity?.nameG,
                  currency: currentCurrency?.nameG || 'валют',
                }}
              />
            ) : (
              <FormattedMessage id="table.banks.mainTitle" />
            )}
          </Title>
        )}

        {!currency && (
          <CurrencyFilters
            primaryOptions={currencyOptions}
            onChange={onChangeFilter}
            value={currentOption}
            optionsList={currencyList}
          />
        )}
      </Header>
      <InputWrapper>
        <Icon name="search" style={{ marginRight: '10px' }} />
        <SearchInput
          placeholder={formatMessage({
            id: 'searchBankName',
          })}
          value={searchText}
          onChange={({ target: { value } }) => setSearchText(value)}
        />
      </InputWrapper>

      {sortedBanksList.length > 0 && filteredRowsTitle.length > 0 && (
        <BanksListWrapper>
          <BanksList>
            <TableHead>
              <Row>
                {filteredRowsTitle &&
                  filteredRowsTitle.map(el => (
                    <TableTitle key={el.name}>
                      <TableTitleContent
                        name={el.name}
                        align={el.align}
                        selected={selectedColumn === el.name}
                      >
                        {formatMessage({
                          id: `tableHeader.${el.name}`,
                        })}
                      </TableTitleContent>
                    </TableTitle>
                  ))}
              </Row>

              <MobileRow>
                <MobileSort onClick={handleOpenSorting}>
                  <Icon name="sortIcon" />

                  <MobileSortContent>
                    {formatMessage({
                      id: `tableHeader.${selectedColumn || 'default'}`,
                    })}

                    {selectedColumn && (
                      <MobileSortOption>
                        {formatMessage({
                          id: `tableHeader.${sort[selectedColumn]?.key}.${sort[selectedColumn]?.order}`,
                        })}
                      </MobileSortOption>
                    )}
                  </MobileSortContent>

                  <IconDown isOpen={isOpen} />
                </MobileSort>

                {isOpen && (
                  <ModalSortOptions>
                    <>
                      <ModalSortOptionsTitle>
                        {formatMessage({
                          id: 'sort.type',
                        })}
                      </ModalSortOptionsTitle>
                      <ModalSortTabWrapper>
                        {hasCashColumn && (
                          <ModalSortTab
                            selected={selectedColumn === 'cash'}
                            onClick={() => handleSelectColumn('cash')}
                          >
                            {formatMessage({
                              id: 'tableHeader.cash.short',
                            })}
                          </ModalSortTab>
                        )}

                        <ModalSortTab
                          selected={selectedColumn === 'card'}
                          onClick={() => handleSelectColumn('card')}
                        >
                          {formatMessage({
                            id: 'tableHeader.card.short',
                          })}
                        </ModalSortTab>
                      </ModalSortTabWrapper>
                    </>
                    {selectedColumn && (
                      <>
                        <ModalSortOptionsTitle>
                          {formatMessage({
                            id: 'sort.params',
                          })}
                        </ModalSortOptionsTitle>

                        <ModalSortOptionsList>
                          {optionsList.map(item => {
                            return (
                              <SortedButton
                                key={item.name}
                                onClick={onSort}
                                name={item.key}
                                column={selectedColumn}
                                direction={item.order}
                                selected={
                                  sort?.[selectedColumn]?.key === item.key &&
                                  sort?.[selectedColumn]?.order === item.order
                                }
                              >
                                {formatMessage({
                                  id: item.name,
                                })}
                              </SortedButton>
                            );
                          })}
                        </ModalSortOptionsList>
                      </>
                    )}
                  </ModalSortOptions>
                )}
              </MobileRow>

              <Row>
                <TableTitle>
                  <TableTitleContent />
                </TableTitle>

                {hasCashColumn && (
                  <TableTitle>
                    <TableSubTitleContent>
                      <SortedTableHeaderCell
                        onHeaderClick={onSort}
                        name="bid"
                        column="cash"
                        direction={
                          selectedColumn === 'cash' && sort.cash.key === 'bid'
                            ? sort.cash.order
                            : null
                        }
                      />
                      <span>/</span>
                      <SortedTableHeaderCell
                        onHeaderClick={onSort}
                        name="ask"
                        column="cash"
                        direction={
                          selectedColumn === 'cash' && sort.cash.key === 'ask'
                            ? sort.cash.order
                            : null
                        }
                      />
                    </TableSubTitleContent>
                  </TableTitle>
                )}

                <TableTitle>
                  <TableSubTitleContent>
                    <SortedTableHeaderCell
                      onHeaderClick={onSort}
                      name="bid"
                      column="card"
                      direction={
                        selectedColumn === 'card' && sort.card.key === 'bid'
                          ? sort.card.order
                          : null
                      }
                    />
                    <span>/</span>
                    <SortedTableHeaderCell
                      onHeaderClick={onSort}
                      name="ask"
                      column="card"
                      direction={
                        selectedColumn === 'card' && sort.card.key === 'ask'
                          ? sort.card.order
                          : null
                      }
                    />
                  </TableSubTitleContent>
                </TableTitle>

                <TableTitle>
                  <TableTitleContent />
                </TableTitle>
              </Row>
            </TableHead>

            <tbody>
              {sortedBanksList.map(bank => {
                let maxDate = null;

                if (bank?.cash?.date && bank?.card?.date) {
                  const cashDate = parseDate(bank.cash.date);
                  const cardDate = parseDate(bank.card.date);

                  if (cashDate && cardDate) {
                    maxDate = dayjs.max(cashDate, cardDate);
                  } else {
                    maxDate = cashDate || cardDate;
                  }
                } else if (bank?.cash?.date || bank?.card?.date) {
                  maxDate = parseDate(bank?.cash?.date || bank?.card?.date);
                }

                return (
                  <React.Fragment key={bank.slug}>
                    <Row>
                      <Column>
                        <BankColumn>
                          <BankLogo src={getBankLogo(bank.logo)} />

                          <BankLink href={getHrefBank(bank.slug, locale)}>
                            {bank.name}
                          </BankLink>
                        </BankColumn>
                      </Column>

                      {hasCashColumn && renderCurrency(bank.cash)}
                      {renderCurrency(bank.card)}

                      <ColumnUpdate>
                        <Update>
                          <IconWatch name="watch" />
                          {maxDate
                            ? maxDate.locale(locale).format('DD MMMM HH:mm')
                            : '—'}
                        </Update>
                      </ColumnUpdate>
                    </Row>
                  </React.Fragment>
                );
              })}
            </tbody>
          </BanksList>
        </BanksListWrapper>
      )}

      {sortedBanksList.length > 0 ? (
        <Bottom>
          {!hasParams && (
            <AllBanks {...allBanksButtonProps}>
              <FormattedMessage id="table.banks.all" />
              <Icon name="vectorStrokeMb" style={{ marginLeft: '10px' }} />
            </AllBanks>
          )}
          <Source>
            <FormattedMessage id="table.source.banks.desc" />
          </Source>
          <Disclaimer>
            <DisclaimerIcon name="warningIcon" />
            <FormattedMessage id="table.banks.disclaimer" />
          </Disclaimer>
        </Bottom>
      ) : (
        <FormattedMessage id="noData.noFound" />
      )}
    </Wrapper>
  );
};

const mapStateToProps = state => ({
  banksExchrates: state.banks?.banksExchrates || [],
  currencyList: state.currency?.currencyList || [],
});

export default connect(mapStateToProps)(injectIntl(BanksTable));

BanksTable.propTypes = {
  banksExchrates: PropTypes.arrayOf(PropTypes.shape({})),
  currencyList: PropTypes.arrayOf(PropTypes.shape({})),
  quantity: PropTypes.number,
  title: PropTypes.string,
  hasCashColumn: PropTypes.bool,
  isClientNavigation: PropTypes.bool,
};

BanksTable.defaultProps = {
  banksExchrates: [],
  currencyList: [],
  quantity: null,
  title: null,
  hasCashColumn: true,
  isClientNavigation: false,
};
